import { getRuntimeConfig } from '../config/runtimeConfig';

/**
 * Biletul de acces la WebSocket.
 *
 * Nu există ecran de login: aplicația cere backendului un token de scurtă durată
 * și îl folosește în handshake-ul socket-ului. Cine are voie să primească un
 * bilet decide backendul (rețea locală, autentificare la reverse-proxy, cookie
 * de sesiune existent) — frontendul nu poartă niciodată vreo credențială.
 */
export interface Ticket {
  /** JWT de scurtă durată, valabil doar pentru conexiunea WebSocket. */
  token: string;
  /** Durata de viață, în secunde. */
  expiresIn: number;
}

export class TicketError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'TicketError';
  }
}

function isTicket(value: unknown): value is Ticket {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Ticket).token === 'string' &&
    (value as Ticket).token !== ''
  );
}

/** `GET /auth/ticket`. Cookie-urile merg cu cererea, dacă backendul le folosește. */
export async function fetchTicket(signal?: AbortSignal): Promise<Ticket> {
  const config = getRuntimeConfig();
  const base = config.apiBaseUrl.replace(/\/$/, '');

  // Antetul se pune doar dacă există o cheie: fără el, backendul decide după
  // rețea, cum făcea înainte. Așa aceeași build merge și în LAN, fără cheie,
  // și expusă public, cu una.
  const headers: HeadersInit = config.accessKey === '' ? {} : { 'X-Api-Key': config.accessKey };

  let res: Response;
  try {
    res = await fetch(`${base}/auth/ticket`, { credentials: 'include', headers, signal });
  } catch {
    throw new TicketError('Serverul nu răspunde.', 0);
  }

  if (res.status === 401 || res.status === 403) {
    throw new TicketError('Acces neautorizat la stație.', res.status);
  }
  if (!res.ok) {
    throw new TicketError('Stația a răspuns cu o eroare.', res.status);
  }

  const body: unknown = await res.json();
  if (!isTicket(body)) {
    throw new TicketError('Răspuns neașteptat de la stație.', res.status);
  }

  return { token: body.token, expiresIn: Number(body.expiresIn) || 0 };
}
